import { faro } from "@grafana/faro-react";

/**
 * Utility function to ensure exhaustive type checking in switch statements.
 * This function should be called in the default case of switch statements
 * that handle all possible values of a union type.
 *
 * @param value - The value that should never be reached
 * @param context - Optional context about where this was called (e.g., function name, component name)
 * @throws Error if the function is called
 */
export function assertNever(
  value: never,
  context?: {
    functionName?: string;
    componentName?: string;
    fileName?: string;
    additionalInfo?: Record<string, unknown>;
  },
): never {
  const error = new Error(`Unhandled value: ${JSON.stringify(value)}`);
  const stackTrace = error.stack ?? "No stack trace available";

  // Extract the caller's location from stack trace
  const stackLines = stackTrace.split("\n");
  const callerLine = stackLines[2]?.trim() ?? "Unknown location";

  // Log to Grafana with detailed context
  faro.api.pushError(error, {
    type: "assertNever",
    context: {
      value: JSON.stringify(value),
      stackTrace,
      callerLocation: callerLine,
      functionName: context?.functionName ?? "Unknown function",
      componentName: context?.componentName ?? "Unknown component",
      fileName: context?.fileName ?? "Unknown file",
      additionalInfo: JSON.stringify(context?.additionalInfo ?? {}),
      timestamp: new Date().toISOString(),
    },
  });

  throw error;
}
